import { InfoCircleFilled } from "@ant-design/icons";
import {
  AddonPlatform,
  DashboardSpreadsheet,
  DashboardWriteback,
  DataSourceType,
  WritebackRunOverallStatusType,
} from "@coeff/api";
import { History } from "history";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Route, Switch, useHistory, useRouteMatch } from "react-router-dom";
import styled from "styled-components";

import {
  ImportTypeIcon,
  InputSearch,
  Option,
  FilledSelect,
  GoogleSheetsIcon,
  DownArrow,
  LoaderWithPerfTimings,
  ExcelSheetsIcon,
  Typography,
} from "../../../components";
import { COLORS } from "../../../constants";
import { DataSourceTypeDisplayNames, getRefreshedDate, useTrack } from "../../../utils";
import { Header } from "../components";
import { useDashboardContext } from "../Dashboard";

import { SpreadsheetDetails } from "./SpreadsheetDetails";

const Container = styled.div`
  padding: 20px 30px;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;

  .coeff__input-search {
    margin: 0;
    max-width: 360px;
  }
`;

const ToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SheetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(340px, 1fr));
  grid-auto-rows: minmax(210px, auto);
  gap: 20px;

  @media (max-width: 1420px) {
    grid-template-columns: repeat(2, minmax(340px, 1fr));
  }
`;

const SheetCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: 23px 20px 0px;

  cursor: pointer;
  border: 1px solid #d8d8d8;
  border-radius: 8px;

  &:hover {
    box-shadow: 0px 10px 15px rgba(0, 0, 0, 0.1);
  }
`;

const CardHeader = styled.div`
  display: flex;
  flex: 1;

  div {
    flex: 1;
  }

  h4 {
    font-weight: 700;
    font-size: 18px;
    line-height: 22px;
  }

  span {font-weight: 500;
    font-size: 14px;
    line-height: 16px;
  }

  svg {
    float: right:
  }
`;

const CardFooter = styled.div`
  display: flex;
  margin-bottom: 15px;

  .ant-avatar {
    margin: 0 3px 0 0;
    height: 40px;
    width: 40px;

    svg {
      height: 40px;
      width: 40px;
    }
  }
`;

const ErrorFooter = styled.div`
  background: ${COLORS.redBg};
  margin: auto -20px;
  padding: 5px 20px;
  border-bottom-right-radius: 8px;
  border-bottom-left-radius: 8px;
  font-weight: bold;

  .anticon {
    color: ${COLORS.red};
    margin-right: 8px;
  }
`;

const ExtraDataSourcesUsed = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  width: 40px;
  border-radius: 50%;
  background-color: ${COLORS.coeblue4};
  color: ${COLORS.white};
  font-weight: 700;
  font-size: 14px;
  line-height: 17px;
`;

const ShowAction = styled.span`
  cursor: pointer;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  margin-top: 10px;
`;

const StyledInputSearch = styled(InputSearch)`
  width: 100%;
  max-width: 360px;
  margin-right: 8px;
`;

const spreadsheetLatestRunDtIso = (sheet: DashboardSpreadsheet): string | undefined => {
  const writebacks = sheet.writebacks ?? [];
  const dates = [
    ...sheet.data_imports.map(di => di.last_run_dt).filter(Boolean),
    ...writebacks.map(wb => wb.last_run_dt).filter(Boolean),
  ] as string[];
  if (dates.length === 0) {
    return undefined;
  }
  return dates.reduce((latest, d) => (moment(d).isAfter(moment(latest)) ? d : latest));
};

const isWritebackRunInError = (wb: DashboardWriteback): boolean => {
  const st = wb.last_writeback_run_status?.overall_status;
  if (st === undefined) {
    return false;
  }
  return st !== WritebackRunOverallStatusType.Ok;
};

const renderSheetCard = (sheet: DashboardSpreadsheet, history: History<unknown>, url: string) => {
  const writebacks = sheet.writebacks ?? [];
  const latestRunDtIso = spreadsheetLatestRunDtIso(sheet);

  const sheetDataSourcesUsed = [
    ...new Set([
      ...sheet.data_imports.map(di => di.data_source_type),
      ...writebacks.map(wb => wb.data_source_type),
    ]),
  ];

  const onClick = () => history.push(`${url}/${sheet.google_sheets_file_id}`);

  const errorImports = sheet.data_imports.filter(
    di => di.last_import_status?.status_level === "error"
  );
  const errorWritebacks = writebacks.filter(isWritebackRunInError);
  const errorRunCount = errorImports.length + errorWritebacks.length;

  return (
    <SheetCard key={sheet.google_sheets_file_id} onClick={onClick}>
      <CardHeader>
        <div style={{ minWidth: 0 }}>
          <Typography fontSize="18px" fontWeight={700} ellipsis>
            {sheet.google_sheets_file_name}
          </Typography>

          {latestRunDtIso && (
            <span>Last refreshed {getRefreshedDate(moment.utc(latestRunDtIso).local())}</span>
          )}
        </div>

        {sheet.spreadsheet_platform === AddonPlatform.GoogleSheets ? (
          <GoogleSheetsIcon />
        ) : (
          <ExcelSheetsIcon />
        )}
      </CardHeader>

      <CardFooter>
        {sheetDataSourcesUsed.slice(0, 6).map(ds => (
          <ImportTypeIcon key={ds} type={ds} />
        ))}
        {sheetDataSourcesUsed.length > 6 && (
          <ExtraDataSourcesUsed>+{sheetDataSourcesUsed.length - 6}</ExtraDataSourcesUsed>
        )}
      </CardFooter>

      {errorRunCount > 0 && (
        <ErrorFooter>
          <InfoCircleFilled />
          {errorRunCount} run{errorRunCount > 1 ? "s" : ""} failed
        </ErrorFooter>
      )}
    </SheetCard>
  );
};

export const Spreadsheets = () => {
  const track = useTrack();

  const { spreadsheets, fetchingSheets } = useDashboardContext();

  const history = useHistory();
  const { path, url } = useRouteMatch();

  const [searchValue, setSearchValue] = useState<string>("");

  const [selectedDataSourceFilter, setSelectedDataSourceFilter] = useState("all");

  const [selectedSpreadsheetSort, setSelectedSpreadsheetSort] = useState("date");

  const [showAll, setShowAll] = useState<boolean>(false);

  let spreadsheetsToShow = spreadsheets ?? [];

  const { dataSourcesBySpreadsheet, dataSourcesUsed } = spreadsheets.reduce(
    (
      acc: {
        dataSourcesBySpreadsheet: object;
        dataSourcesUsed: DataSourceType[];
      },
      sheet
    ) => {
      const sheetDataSourcesUsed = [
        ...new Set([
          ...sheet.data_imports.map(di => di.data_source_type),
          ...(sheet.writebacks ?? []).map(wb => wb.data_source_type),
        ]),
      ];

      const uniqueDataSources = [...new Set([...acc.dataSourcesUsed, ...sheetDataSourcesUsed])];

      acc.dataSourcesBySpreadsheet[sheet.google_sheets_file_id] = sheetDataSourcesUsed;

      acc.dataSourcesUsed = uniqueDataSources.sort((a, b) =>
        DataSourceTypeDisplayNames[a].localeCompare(DataSourceTypeDisplayNames[b])
      );

      return acc;
    },
    { dataSourcesBySpreadsheet: {}, dataSourcesUsed: [] }
  );

  useEffect(() => {
    document.title = "Spreadsheets - Coefficient Workspace";
    track("workspace_spreadsheets_viewed");
  }, []);

  if (searchValue) {
    spreadsheetsToShow = spreadsheetsToShow.filter(s =>
      (s.google_sheets_file_name ?? "").toLowerCase().includes(searchValue.toLowerCase())
    );
  }

  if (selectedDataSourceFilter !== "all") {
    spreadsheetsToShow = spreadsheetsToShow.filter(s =>
      dataSourcesBySpreadsheet[s.google_sheets_file_id].includes(selectedDataSourceFilter)
    );
  }

  spreadsheetsToShow = spreadsheetsToShow.sort((a, b) => {
    if (selectedSpreadsheetSort === "date") {
      const aDt = spreadsheetLatestRunDtIso(a);
      const bDt = spreadsheetLatestRunDtIso(b);

      if (aDt === undefined && bDt === undefined) {
        return 0;
      }
      if (aDt === undefined) {
        return 1;
      }
      if (bDt === undefined) {
        return -1;
      }

      return moment(bDt).diff(moment(aDt));
    } else {
      return (a.google_sheets_file_name ?? "")
        .toLowerCase()
        .localeCompare((b.google_sheets_file_name ?? "").toLowerCase());
    }
  });

  return (
    <Switch>
      <Route exact path={path}>
        <Container>
          <Header>
            <h1>Spreadsheets</h1>
          </Header>

          {fetchingSheets && (
            <div style={{ margin: "5rem auto 2rem", textAlign: "center" }}>
              <LoaderWithPerfTimings name="Spreadsheets" size="large" />
            </div>
          )}

          {!fetchingSheets && (
            <div style={{ maxWidth: "1200px" }}>
              <Toolbar>
                <div style={{ flex: "1" }}>
                  <StyledInputSearch
                    onChange={e => setSearchValue(e.target.value)}
                    value={searchValue}
                  />
                </div>

                <ToolbarActions>
                  <FilledSelect
                    suffixIcon={<DownArrow />}
                    onChange={filter => setSelectedDataSourceFilter(String(filter))}
                    value={selectedDataSourceFilter}
                  >
                    <Option value="all">All Data Sources</Option>

                    {dataSourcesUsed.map((ds, i) => (
                      <Option key={i} value={ds}>
                        {DataSourceTypeDisplayNames[ds]}
                      </Option>
                    ))}
                  </FilledSelect>

                  <FilledSelect
                    suffixIcon={<DownArrow />}
                    onChange={sort => setSelectedSpreadsheetSort(String(sort))}
                    value={selectedSpreadsheetSort}
                    defaultValue="all"
                  >
                    <Option value="date">Sort: Last Refreshed</Option>
                    <Option value="alpha">Sort: Alphabetical</Option>
                  </FilledSelect>
                </ToolbarActions>
              </Toolbar>

              <SheetGrid>
                {spreadsheetsToShow.map(
                  (sheet, i) => (showAll || i < 15) && renderSheetCard(sheet, history, url)
                )}
              </SheetGrid>

              <div style={{ margin: "1rem auto" }}>
                {spreadsheetsToShow.length > 15 && (
                  <ShowAction onClick={() => setShowAll(!showAll)}>
                    Show {showAll ? "less" : "all"} sheets
                  </ShowAction>
                )}
              </div>
            </div>
          )}
        </Container>
      </Route>

      <Route path={`${path}/:ssId`}>
        <SpreadsheetDetails />
      </Route>
    </Switch>
  );
};
